/**
 * Recovery for the "Failed to fetch dynamically imported module" error.
 *
 * Every route in `router.tsx` is code-split into a hashed JS chunk (e.g.
 * `LoginPage-BaEOQOqY.js`). When we deploy a new admin image, the hashes
 * change — any browser tab that was already open before the deploy is still
 * running the OLD `index.html` and will ask the server for chunk names that
 * no longer exist.
 *
 * The browser surfaces this as a `TypeError: Failed to fetch dynamically
 * imported module` (or Chrome's `ChunkLoadError`). The fix is simple: the
 * tab just needs to load the new `index.html`, which points at the new
 * chunk hashes. We detect the error and trigger a single reload.
 *
 * A session-scoped flag prevents reload loops when the failure is caused by
 * something other than a stale client (e.g. the admin CDN is actually down).
 */

const RELOAD_FLAG_KEY = 'salaamah.chunk-reload-attempted-at';
const RELOAD_GRACE_MS = 15_000;

function isStaleChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  // Chrome/Safari use `Failed to fetch dynamically imported module`.
  // Firefox uses `error loading dynamically imported module`.
  // Some bundlers tag a named `ChunkLoadError`.
  const msg = error.message.toLowerCase();
  return (
    error.name === 'ChunkLoadError' ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('importing a module script failed')
  );
}

function alreadyReloadedRecently(): boolean {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_FLAG_KEY);
    if (!raw) return false;
    const ts = Number.parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < RELOAD_GRACE_MS;
  } catch {
    // sessionStorage can throw in sandboxed iframes / cookie-blocked browsers.
    // Err on the side of NOT reloading to avoid loops we can't detect.
    return true;
  }
}

function markReloadAttempt(): void {
  try {
    window.sessionStorage.setItem(RELOAD_FLAG_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

/**
 * Wraps a dynamic `import()` factory so that a stale-chunk failure triggers
 * a single page reload (which re-fetches the fresh `index.html`). On the
 * second failure within {@link RELOAD_GRACE_MS} we rethrow so the caller's
 * error boundary can render something useful.
 */
export function withChunkRecovery<T>(
  loader: () => Promise<T>,
): () => Promise<T> {
  return async () => {
    try {
      return await loader();
    } catch (error) {
      if (isStaleChunkError(error) && !alreadyReloadedRecently()) {
        markReloadAttempt();
        window.location.reload();
        // Return a never-resolving promise so React Suspense keeps the
        // previous UI on-screen until the reload actually kicks in. If we
        // rethrew here, the user would briefly see the error boundary.
        return new Promise<T>(() => {
          void 0;
        });
      }
      throw error;
    }
  };
}
